import { type ChangeEvent, type ComponentPropsWithoutRef } from 'react';
import type {
  FieldPath,
  FieldValues,
  UseControllerReturn,
} from 'react-hook-form';

import { TextField } from '@breezeventures/ui-kit/text-field/rhf';

type InputProps = ComponentPropsWithoutRef<typeof TextField>;

export type InputNumberProps<
  Values extends FieldValues = FieldValues,
  Name extends FieldPath<Values> = FieldPath<Values>,
> = InputProps &
  UseControllerReturn<Values, Name> & {
    allowDecimal?: boolean;
  };

const sanitize = (raw: string, allowDecimal: boolean): string => {
  if (!allowDecimal) {
    return raw.replace(/\D/g, '');
  }

  const [integerPart, ...fractionParts] = raw.replace(/[^\d.]/g, '').split('.');

  return fractionParts.length > 0
    ? `${integerPart}.${fractionParts.join('')}`
    : integerPart;
};

export const InputNumber = <
  Values extends FieldValues = FieldValues,
  Name extends FieldPath<Values> = FieldPath<Values>,
>({
  allowDecimal = false,
  ...props
}: InputNumberProps<Values, Name>) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    props.field.onChange({
      ...event,
      target: {
        ...event.target,
        value: sanitize(event.target.value, allowDecimal),
      },
    });
  };

  return (
    <TextField
      {...props}
      value={props.field.value ?? ''}
      inputMode={allowDecimal ? 'decimal' : 'numeric'}
      onChange={handleChange}
    />
  );
};
